import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, Repeat, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { useBulkDeleteCli, useCliList, useDeallocateCli } from '../api';
import { cliStatusLabel, type CliRow, type CliType } from '../types';

export function CliListPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const userId = useAuthStore((s) => s.userId);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const isAdmin = role === USER_ROLES.ADMIN;
  const isSadmin = role === USER_ROLES.S_ADMIN;

  // SADMIN can toggle; SUB_SUPER_ADMIN defaults to 'cli', ADMIN to 'dedicated_cli'.
  const initialCliType: CliType = isAdmin ? 'dedicated_cli' : 'cli';
  const [cliType, setCliType] = useState<CliType>(initialCliType);

  const query = useCliList(cliType);
  const rows = query.data ?? [];

  const deallocate = useDeallocateCli();
  const bulkDelete = useBulkDeleteCli();

  const [pendingDelete, setPendingDelete] = useState<CliRow | null>(null);
  const [pendingBulk, setPendingBulk] = useState<{ ids: string[]; clear: () => void } | null>(
    null,
  );

  const columns = useMemo<ColumnDef<CliRow>[]>(() => {
    const base: ColumnDef<CliRow>[] = [
      {
        accessorKey: 'cliId',
        header: 'CLI ID',
        size: 100,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.cliId}
          </span>
        ),
      },
      { accessorKey: 'cli', header: 'CLI' },
    ];

    if (isSubOrSuper) {
      base.push({
        accessorKey: 'cliStatus',
        header: 'Status',
        size: 120,
        cell: ({ row }) => cliStatusLabel(row.original.cliStatus),
      });
    }

    base.push(
      { accessorKey: 'dialerName', header: 'Dialer', size: 160 },
      { accessorKey: 'pilotNumber', header: 'Pilot No.', size: 140 },
    );

    if (cliType === 'dedicated_cli') {
      base.push(
        {
          id: 'userName',
          header: 'Username',
          size: 160,
          accessorFn: (r) =>
            r.cliStatus === 2 ? r.userName ?? '' : r.resellerUsername ?? '',
        },
        {
          accessorKey: 'allocationDate',
          header: 'Allocated',
          size: 180,
        },
      );
    }

    if (isSubOrSuper) {
      base.push({
        id: 'actions',
        header: '',
        size: 60,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel className="font-mono">
                {row.original.cli}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate(`/update-cli/${row.original.cliId}`)}
              >
                <Pencil className="h-4 w-4" />
                Edit value
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Deallocate
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      });
    }

    return base;
  }, [cliType, isSubOrSuper, navigate]);

  const confirmDelete = () => {
    if (!pendingDelete) return;
    deallocate.mutate(
      {
        cliId: pendingDelete.cliId,
        userId: Number(userId),
        allocated: isSubOrSuper ? 1 : 2,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `Deallocated ${pendingDelete.cli}`);
          setPendingDelete(null);
        },
        onError: () => toast.error('Could not deallocate the CLI.'),
      },
    );
  };

  const confirmBulkDelete = () => {
    if (!pendingBulk) return;
    bulkDelete.mutate(pendingBulk.ids.map(Number), {
      onSuccess: (res) => {
        toast.success(res?.message ?? `Deleted ${pendingBulk.ids.length} CLIs`);
        pendingBulk.clear();
        setPendingBulk(null);
      },
      onError: () => toast.error('Bulk delete failed.'),
    });
  };

  return (
    <FillPage>
      <PageHeader
        title={cliType === 'cli' ? 'All CLIs' : 'Dedicated CLIs'}
        description="All caller-line identities visible to your account."
        actions={
          <div className="flex items-center gap-2">
            {isSadmin && (
              <Button
                variant="outline"
                onClick={() =>
                  setCliType((t) => (t === 'cli' ? 'dedicated_cli' : 'cli'))
                }
              >
                <Repeat className="h-4 w-4" />
                {cliType === 'cli' ? 'Show dedicated' : 'Show shared'}
              </Button>
            )}
            {isSubOrSuper && (
              <>
                <Button asChild variant="outline">
                  <Link to="/add-cli">
                    <Plus className="h-4 w-4" />
                    Allocate to user
                  </Link>
                </Button>
                <Button asChild>
                  <Link
                    to={cliType === 'cli' ? '/upload-cli' : '/upload-dedicated-cli'}
                  >
                    <Upload className="h-4 w-4" />
                    Upload {cliType === 'cli' ? 'CLI' : 'dedicated CLI'}
                  </Link>
                </Button>
              </>
            )}
          </div>
        }
      />

      <DataTable<CliRow, unknown>
        tableId={`cli:list:${cliType}:v1`}
        columns={columns}
        data={rows}
        isLoading={query.isLoading}
        searchPlaceholder="Search CLIs…"
        emptyTitle="No CLIs found"
        emptyDescription="Upload a CLI file or allocate CLIs to populate this list."
        getRowId={(row) => String(row.cliId)}
        exportFilename={`${cliType}.csv`}
        initialColumnPinning={isSubOrSuper ? { right: ['actions'] } : undefined}
        pageSize={10}
        enableRowSelection={isSubOrSuper}
        renderSelectionActions={(ids, clear) => (
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setPendingBulk({ ids, clear })}
            disabled={ids.length === 0 || bulkDelete.isPending}
          >
            <Trash2 className="h-4 w-4" />
            Delete {ids.length}
          </Button>
        )}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            CLI <span className="font-medium text-foreground">{pendingDelete?.cli}</span> will
            be deallocated.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deallocate.isPending}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={pendingBulk !== null}
        onOpenChange={(o) => !o && setPendingBulk(null)}
        title="Are you sure you want to delete?"
        description={
          <>
            <span className="font-medium text-foreground">
              {pendingBulk?.ids.length ?? 0}
            </span>{' '}
            selected CLIs will be permanently deleted.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={bulkDelete.isPending}
        onConfirm={confirmBulkDelete}
      />
    </FillPage>
  );
}
